import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Switch, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import * as api from '../plannerApi';
import * as T from '../types';
import { Loader, NeedsHousehold, SectionTitle, useLoad, useNotify } from '../common';
import { TAGS, tagText } from '../labels';
import { PLAN_PATH } from './shared';
import { useGoBack } from '../editing';

type Limits = Record<string, { min: string; max: string }>;

const emptyLimits = (): Limits => {
  const limits: Limits = {};
  TAGS.forEach(t => { limits[t.value] = { min: '', max: '' }; });
  return limits;
};

/** Empty → null; otherwise a whole number from 0 to 14, else undefined (invalid). */
function parseLimit(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  const value = Number(trimmed);
  return value <= 14 ? value : undefined;
}

function RulesForm({ settings }: { settings: T.MealSuggestionSettings }) {
  const goBack = useGoBack(PLAN_PATH);
  const notify = useNotify();
  const [noRepeat, setNoRepeat] = useState(String(settings.noRepeatDays));
  const [quick, setQuick] = useState(settings.weekdayDinnerQuickOnly);
  const [leftovers, setLeftovers] = useState(settings.preferLeftoverFriendly);
  const [limits, setLimits] = useState<Limits>(() => {
    const initial = emptyLimits();
    settings.quotas.forEach(q => {
      if (initial[q.tag]) initial[q.tag] = { min: q.min?.toString() ?? '', max: q.max?.toString() ?? '' };
    });
    return initial;
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const setLimit = (tag: T.RecipeTag, field: 'min' | 'max', value: string) =>
    setLimits(current => ({ ...current, [tag]: { ...current[tag], [field]: value } }));

  const save = async () => {
    setError(null);
    const days = noRepeat.trim();
    if (!/^\d+$/.test(days)) return setError('Indica um número de dias (0 = pode repetir).');

    const quotas: T.TagQuota[] = [];
    for (let i = 0; i < TAGS.length; i++) {
      const tag = TAGS[i].value;
      const min = parseLimit(limits[tag].min);
      const max = parseLimit(limits[tag].max);
      if (min === undefined || max === undefined) return setError(`${tagText(tag)}: os limites devem ser números de 0 a 14.`);
      if (min !== null || max !== null) quotas.push({ tag, min, max });
    }

    setBusy(true);
    try {
      await api.updateSuggestionSettings({ noRepeatDays: Number(days), weekdayDinnerQuickOnly: quick, preferLeftoverFriendly: leftovers, quotas });
      notify('Regras guardadas.', { severity: 'success' });
      goBack();
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
      setBusy(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <TextField
        label="Não repetir uma receita durante (dias)" value={noRepeat} onChange={e => setNoRepeat(e.target.value)}
        slotProps={{ htmlInput: { inputMode: 'numeric' } }} sx={{ maxWidth: 360 }}
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="subtitle2">Jantares em dia útil: só rápidas</Typography>
          <Typography variant="caption" color="text.secondary">Receitas até 30 min ou marcadas como rápidas.</Typography>
        </Box>
        <Switch checked={quick} onChange={e => setQuick(e.target.checked)} slotProps={{ input: { 'aria-label': 'Jantares em dia útil: só rápidas' } }} />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="subtitle2">Preferir receitas que dão sobras</Typography>
          <Typography variant="caption" color="text.secondary">Nas refeições cujas sobras são comidas a seguir.</Typography>
        </Box>
        <Switch checked={leftovers} onChange={e => setLeftovers(e.target.checked)} slotProps={{ input: { 'aria-label': 'Preferir receitas que dão sobras' } }} />
      </Stack>

      <Box>
        <SectionTitle>Variedade por semana</SectionTitle>
        <Typography variant="caption" color="text.secondary">Mínimo e máximo de refeições com cada etiqueta (em branco = sem regra).</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 72px 72px', columnGap: 1, rowGap: 1, alignItems: 'center', maxWidth: 420 }}>
        <Typography variant="caption" color="text.secondary">Etiqueta</Typography>
        <Typography variant="caption" color="text.secondary" align="center">Mín.</Typography>
        <Typography variant="caption" color="text.secondary" align="center">Máx.</Typography>
        {TAGS.map(t => (
          <React.Fragment key={t.value}>
            <Typography variant="body2">{t.text}</Typography>
            <TextField
              size="small" value={limits[t.value].min} onChange={e => setLimit(t.value, 'min', e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'numeric', style: { textAlign: 'center' }, 'aria-label': `${t.text}: mínimo` } }}
            />
            <TextField
              size="small" value={limits[t.value].max} onChange={e => setLimit(t.value, 'max', e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'numeric', style: { textAlign: 'center' }, 'aria-label': `${t.text}: máximo` } }}
            />
          </React.Fragment>
        ))}
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      <Box>
        <Button variant="contained" onClick={save} disabled={busy} startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}>
          Guardar
        </Button>
      </Box>
    </Stack>
  );
}

function RulesContent() {
  const goBack = useGoBack(PLAN_PATH);
  const { data, error, loading, reload } = useLoad(api.getSuggestionSettings);

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton aria-label="Voltar" edge="start" onClick={goBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>Regras das sugestões</Typography>
      </Stack>
      <Loader loading={loading} error={error} onRetry={reload} hasData={data !== undefined}>
        {data && <RulesForm settings={data} />}
      </Loader>
    </Box>
  );
}

/** The household's rules for meal suggestions: no-repeat window, quick weekday dinners, leftovers, tag quotas. */
const SuggestionRulesPage: React.FC = () => <NeedsHousehold><RulesContent /></NeedsHousehold>;

export default SuggestionRulesPage;
